import React from 'react';
import {Box, Checkbox, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {keyframes} from '@mui/system';

const createdFolderFlash = keyframes`
    0% { background-color: #ffffff; }
    100% { background-color: #fff2b8; }
`;

const SourcesFolderList = ({
    folders,
    onFolderClick,
    onOpenFolderMenu,
    onFolderDrop,
    dropEnabled,
    canDropToFolder,
    draggable = false,
    canDragFolder,
    onFolderDragStart,
    isFolderSelectable,
    isFolderSelected,
    onFolderSelectionToggle,
    flashFolderId,
    flashFolderToken,
}) => {
    const [hoveredFolderId, setHoveredFolderId] = React.useState(null);
    const [selectionHoverFolderId, setSelectionHoverFolderId] = React.useState(null);

    if (!folders?.length) {
        return null;
    }

    return (
        <List dense sx={{p: 0}}>
            {folders.map((folder) => {
                const folderDropEnabled = dropEnabled && (
                    typeof canDropToFolder === 'function' ? canDropToFolder(folder) : true
                );
                const folderDragEnabled = draggable && (
                    typeof canDragFolder === 'function' ? canDragFolder(folder) : true
                );
                const folderSelectable = typeof isFolderSelectable === 'function' ? isFolderSelectable(folder) : false;
                const folderSelected = typeof isFolderSelected === 'function' ? isFolderSelected(folder) : false;
                const isFlashingCreatedFolder = Boolean(flashFolderId) && String(flashFolderId) === String(folder.id);
                const rowKey = isFlashingCreatedFolder ? `${folder.id}:${flashFolderToken}` : folder.id;

                return (
                <ListItem
                    key={rowKey}
                    disablePadding
                    draggable={folderDragEnabled}
                    onDragStart={(event) => {
                        if (!folderDragEnabled) {
                            return;
                        }
                        onFolderDragStart?.(event, folder);
                    }}
                    onMouseEnter={() => setSelectionHoverFolderId(folder.id)}
                    onMouseLeave={() => setSelectionHoverFolderId((current) => (current === folder.id ? null : current))}
                    onDragOver={(event) => {
                        if (!dropEnabled) {
                            return;
                        }

                        if (!folderDropEnabled) {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onDragEnter={(event) => {
                        if (!dropEnabled) {
                            return;
                        }

                        if (!folderDropEnabled) {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        setHoveredFolderId(folder.id);
                    }}
                    onDragLeave={(event) => {
                        if (!dropEnabled) {
                            return;
                        }

                        if (!folderDropEnabled) {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.currentTarget.contains(event.relatedTarget)) {
                            return;
                        }
                        setHoveredFolderId((currentId) => (currentId === folder.id ? null : currentId));
                    }}
                    onDrop={(event) => {
                        if (!dropEnabled) {
                            return;
                        }

                        if (!folderDropEnabled || typeof onFolderDrop !== 'function') {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        setHoveredFolderId(null);
                        onFolderDrop(folder, event);
                    }}
                    sx={(theme) => ({
                        bgcolor: hoveredFolderId === folder.id
                            ? '#e3f2fd'
                            : (folderSelected ? theme.palette.action.selected : 'white'),
                        mb: 1,
                        borderRadius: 1,
                        border: hoveredFolderId === folder.id
                            ? '1px solid #1976d2'
                            : (folderSelected ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent'),
                        outline: folderSelected ? `2px solid ${theme.palette.secondary.main}` : 'none',
                        outlineOffset: '-2px',
                        animation: isFlashingCreatedFolder
                            ? `${createdFolderFlash} 220ms ease-in-out 0s 6 alternate`
                            : 'none',
                        transition: 'background-color 120ms ease, border-color 120ms ease, outline-color 120ms ease',
                    })}
                >
                    <ListItemButton onClick={() => onFolderClick(folder)} sx={{borderRadius: 1}}>
                        <ListItemIcon sx={{minWidth: 34}}>
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {folderSelectable && (selectionHoverFolderId === folder.id || folderSelected) ? (
                                    <Checkbox
                                        size="small"
                                        edge="start"
                                        checked={folderSelected}
                                        sx={{p: 0, m: 0, width: 20, height: 20}}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onFolderSelectionToggle?.(event, folder);
                                        }}
                                    />
                                ) : (
                                    <FolderOutlinedIcon fontSize="small"/>
                                )}
                            </Box>
                        </ListItemIcon>
                        <ListItemText
                            primary={folder.name}
                            secondary={folder.fileCount > 0 ? `${folder.fileCount} files` : null}
                            primaryTypographyProps={{
                                fontWeight: 500,
                                fontSize: '0.95rem',
                            }}
                        />
                        {!folder.isPreset && !folder.isSystem && (
                            <IconButton
                                aria-label={`folder actions for ${folder.name}`}
                                edge="end"
                                size="small"
                                onClick={(event) => onOpenFolderMenu(event, folder)}
                            >
                                <MoreVertIcon fontSize="small"/>
                            </IconButton>
                        )}
                    </ListItemButton>
                </ListItem>
                );
            })}
        </List>
    );
};

export default React.memo(SourcesFolderList);
